import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx'; 

// SVG icons as components
const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M14.74 9l-.346 9h-4.788l-.346-9h5.48zm.346-5a1 1 0 011 1v1H19a1 1 0 010 2H5a1 1 0 110-2h2.91V5a1 1 0 011-1h6.18z" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M23 4v6h-6"></path>
    <path d="M1 20v-6h6"></path>
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10"></path>
    <path d="M20.49 15a9 9 0 01-14.85 3.36L1 14"></path>
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const EmailIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

const SubscribersList = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  useEffect(() => {
    fetchSubscribers();
  }, [pagination.current]);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/subscribers2`
      );
      
      // Filter subscribers if search term exists
      let filteredData = response.data;
      if (searchTerm) {
        filteredData = response.data.filter(subscriber => 
          subscriber.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setSubscribers(filteredData);
      setPagination({
        ...pagination,
        total: filteredData.length,
      });
      setError('');
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      setError('Failed to load subscribers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({
      ...pagination,
      current: 1,
    });
    fetchSubscribers();
  };

  const handleDeleteSubscriber = async (id) => {
    if (window.confirm('Are you sure you want to delete this subscriber?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_BACKEND_BASEURL}/api/subscribers2/${id}`);
        fetchSubscribers();
      } catch (error) {
        console.error('Error deleting subscriber:', error);
        setError('Failed to delete subscriber. Please try again.');
      }
    }
  };

  // Export to Excel functionality
  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      // Format data for Excel
      const worksheetData = subscribers.map((subscriber, index) => ({
        '#': index + 1,
        'Email': subscriber.email,
        'Date Subscribed': new Date(subscriber.createdAt).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        'ID': subscriber._id
      }));
      
      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Subscribers");
      
      // Auto-size columns
      const maxWidths = {};
      
      // Get maximum width for each column including header
      if (worksheetData.length > 0) {
        Object.keys(worksheetData[0]).forEach((key, i) => {
          const columnIndex = i;
          maxWidths[columnIndex] = key.length;
          
          worksheetData.forEach(row => {
            const cellValue = row[key] || '';
            const cellWidth = String(cellValue).length;
            maxWidths[columnIndex] = Math.max(maxWidths[columnIndex], cellWidth);
          });
        });
        
        // Set column widths
        worksheet['!cols'] = Object.keys(maxWidths).map(key => ({
          wch: maxWidths[key] + 2 // Adding padding
        }));
      }
      
      // Generate Excel file
      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `subscribers_export_${today}.xlsx`);
      
      alert("Subscribers exported successfully!");
    } catch (error) {
      console.error("Failed to export subscribers", error);
      alert("Failed to export subscribers to Excel");
    } finally {
      setExportLoading(false);
    }
  };

  // Calculate pagination
  const startIndex = (pagination.current - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const paginatedSubscribers = subscribers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(subscribers.length / pagination.pageSize) || 1;

  return (
   
      <div style={{ padding: "30px", background: "#f0f2f5", minHeight: "100vh" }}>
        <div
          style={{
            backgroundColor: "white",
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            padding: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "30px",
              padding: "0 0 16px 0",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <div>
              <h3 style={{ margin: 0, color: "#1890ff" }}>
                Subscribers Management
              </h3>
              <p style={{ color: "#666" }}>
                Manage all your subscribers
              </p>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                style={{
                  backgroundColor: "#52c41a",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 15px",
                  cursor: exportLoading ? "wait" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: exportLoading ? 0.7 : 1,
                }}
                onClick={exportToExcel}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <span>Exporting...</span>
                ) : (
                  <>
                    <DownloadIcon />
                    Export to Excel
                  </>
                )}
              </button>
              <button
                style={{
                  backgroundColor: "#1890ff",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 15px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onClick={fetchSubscribers}
              >
                <RefreshIcon />
                Refresh List
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              marginBottom: "24px",
              background: "#f9fafc",
              padding: "20px",
              borderRadius: "10px",
              border: "1px solid #f0f0f0",
            }}
          >
            <div style={{ position: "relative", width: "100%" }}>
              <input
                type="text"
                placeholder="Search subscribers by email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 10px 10px 36px",
                  borderRadius: "8px",
                  border: "1px solid #d9d9d9",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#bfbfbf",
                }}
              >
                <SearchIcon />
              </span>
            </div>
            <button
              style={{
                backgroundColor: "#1890ff",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "10px 15px",
                marginLeft: "10px",
                cursor: "pointer",
              }}
              onClick={handleSearch}
            >
              Search
            </button>
          </div>

          {/* Stats section */}
          <div 
            style={{
              display: "flex",
              gap: "20px",
              marginBottom: "20px"
            }}
          >
            <div
              style={{
                flex: 1,
                backgroundColor: "#e6f7ff",
                borderRadius: "8px",
                padding: "20px",
                border: "1px solid #91d5ff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <h3 style={{ margin: "0 0 10px 0", color: "#1890ff" }}>Total Subscribers</h3>
              <div 
                style={{ 
                  fontSize: "36px", 
                  fontWeight: "bold", 
                  color: "#1890ff",
                 
                  
                }}
              >
                {subscribers.length}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                backgroundColor: "#f6ffed",
                borderRadius: "8px",
                padding: "20px",
                border: "1px solid #b7eb8f",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <h3 style={{ margin: "0 0 10px 0", color: "#52c41a" }}>Recent Subscribers</h3>
              <div 
                style={{ 
                  fontSize: "36px", 
                  fontWeight: "bold", 
                  color: "#52c41a"
                }}
              >
                {subscribers.filter(sub => {
                  const oneWeekAgo = new Date();
                  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                  return new Date(sub.createdAt) >= oneWeekAgo;
                }).length}
              </div>
              <div style={{ fontSize: "14px", color: "#666" }}>Last 7 days</div>
            </div>
          </div>

          {/* Subscribers Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    borderBottom: "2px solid #f0f0f0",
                    color: "#fff",
                    fontWeight: "bold",
                    background: "linear-gradient(to right, #1890ff, #096dd9)",
                  }}
                >
                  <th style={{ padding: "16px", textAlign: "left", width: "50px" }}>#</th>
                  <th style={{ padding: "16px", textAlign: "left" }}>Email</th>
                  <th style={{ padding: "16px", textAlign: "left" }}>Date Subscribed</th>
                  <th style={{ padding: "16px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="4"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      Loading...
                    </td>
                  </tr>
                ) : paginatedSubscribers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No subscribers found
                    </td>
                  </tr>
                ) : (
                  paginatedSubscribers.map((subscriber, index) => (
                    <tr
                      key={subscriber._id}
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9fafc",
                      }}
                    >
                      <td style={{ padding: "16px" }}>
                        {startIndex + index + 1}
                      </td>
                      <td style={{ padding: "16px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              backgroundColor: "#1890ff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                            }}
                          >
                            <EmailIcon />
                          </div>
                          <span style={{ fontWeight: "500" }}>{subscriber.email}</span>
                        </div>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            backgroundColor: "rgba(0, 201, 167, 0.1)",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            color: "#00c9a7",
                            fontSize: "14px",
                            width: "fit-content",
                          }}
                        >
                          <CalendarIcon />
                          {new Date(subscriber.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }
                          )}{" "}
                          {new Date(subscriber.createdAt).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </div>
                      </td>
                      <td style={{ padding: "16px", textAlign: "center" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            style={{
                              backgroundColor: "#fff1f0",
                              color: "#ff4d4f",
                              border: "none",
                              borderRadius: "6px",
                              padding: "8px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "32px",
                              height: "32px",
                            }}
                            onClick={() => handleDeleteSubscriber(subscriber._id)}
                            title="Delete"
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "20px",
              padding: "10px 0",
            }}
          >
            <div style={{ color: "#666" }}>
              {/* Showing {paginatedSubscribers.length > 0 ? startIndex + 1 : 0} to{" "}
              {Math.min(endIndex, subscribers.length)} of {subscribers.length} subscribers */}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <button
                style={{
                  padding: "5px 10px",
                  borderRadius: "4px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: "white",
                  cursor: pagination.current > 1 ? "pointer" : "not-allowed",
                  opacity: pagination.current > 1 ? 1 : 0.5,
                }}
                onClick={() => {
                  if (pagination.current > 1) {
                    setPagination({
                      ...pagination,
                      current: pagination.current - 1,
                    });
                  }
                }}
                disabled={pagination.current <= 1}
              >
                Previous
              </button>
              <span style={{ padding: "5px 10px" }}>
                Page {pagination.current} of {totalPages}
              </span>
              <button
                style={{
                  padding: "5px 10px",
                  borderRadius: "4px",
                  border: "1px solid #d9d9d9",
                  marginRight:"360px",
                  background: pagination.current < totalPages 
                    ? "linear-gradient(to right, #1890ff, #096dd9)" 
                    : "#f5f5f5",
                  color: pagination.current < totalPages ? "white" : "#bfbfbf",
                  cursor: pagination.current < totalPages ? "pointer" : "not-allowed",
                }}
                onClick={() => {
                  if (pagination.current < totalPages) {
                    setPagination({
                      ...pagination,
                      current: pagination.current + 1,
                    });
                  }
                }}
                disabled={pagination.current >= totalPages }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
  
  );
};

export default SubscribersList;